import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Los componentes de F2b — T-08.
 *
 * Sobre **Base UI**: los autores de Radix, sin estilos. Resuelve lo que se hace
 * mal y no se nota —foco, teclado, ARIA, el orden en que se cierra un diálogo—
 * y deja el aspecto entero a nuestro CSS de tokens. No trae Tailwind ni un
 * segundo sistema de color que pueda contradecir al nuestro.
 *
 * Cada componente aquí **implementa una regla escrita** del sistema de diseño,
 * y sus pruebas comprueban la regla, no el marcado: que el botón deshabilitado
 * no lleve acento importa; que la clase se llame `fr-boton--principal`, no.
 */
import { forwardRef, useId, useRef, useState } from 'react';
import { Dialog } from '@base-ui-components/react/dialog';
import { Switch } from '@base-ui-components/react/switch';
import { Menu } from '@base-ui-components/react/menu';
export const Boton = forwardRef(function Boton({ variante = 'secundario', className, type = 'button', ocupado = false, disabled, ...resto }, ref) {
    return (_jsx("button", { ref: ref, type: type, className: ['fr-boton', `fr-boton--${variante}`, className].filter(Boolean).join(' '), "aria-busy": ocupado || undefined, disabled: ocupado || disabled, ...resto }));
});
/**
 * El ojo de «ver la contraseña» — 2026-09-28, a petición de Isaac.
 *
 * Dos trazos y no una imagen: el párpado y la pupila. Tachado, el mismo dibujo
 * con una raya encima, que es como se lee «apagado» en todas partes.
 *
 * `width`/`height` en el ATRIBUTO y no sólo en la hoja: un `<svg>` con sólo
 * `viewBox` vale el 100 % del ancho, y una regla CSS puede no llegar porque el
 * elemento es reemplazado. Lo vigila `verificar-iconos.mjs`.
 */
function OjoDeContrasena({ tachado }) {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", "aria-hidden": "true", fill: "none", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M1.5 8s2.4-4 6.5-4 6.5 4 6.5 4-2.4 4-6.5 4S1.5 8 1.5 8Z" }), _jsx("circle", { cx: "8", cy: "8", r: "1.9" }), tachado ? _jsx("path", { d: "M2.6 13.4 13.4 2.6" }) : null] }));
}
export const Campo = forwardRef(function Campo({ rotulo, error, ...resto }, ref) {
    // El id se genera aquí para que `label` y `input` queden atados siempre. Si
    // se dejara a quien lo use, el día que se olvide el rótulo dejaría de tocar
    // el campo — y nadie lo notaría salvo quien navega con teclado o lector.
    const id = useId();
    const idError = `${id}-error`;
    /**
     * Ver la contraseña. **Sólo donde el que la usó pidió `type="password"`.**
     *
     * ── Por qué aquí y no en la pantalla que lo pidió ─────────────────────
     * El mismo motivo que `Lista`: una cosa que hay que acordarse de poner se
     * olvida a la cuarta pantalla. Puesto en el componente, toda contraseña
     * futura lo trae.
     *
     * ── Y por qué NO llega al mostrador ──────────────────────────────────
     * Los PIN de `apps/counter` son `<input>` en crudo, no `Campo`, así que
     * esto no los toca — y es lo correcto, no un accidente feliz: la tablet es
     * el aparato más expuesto que hay, con gente detrás en la barra. Un ojo
     * ahí enseña el PIN de una empleada a quien esté esperando su café. Si
     * algún día esos campos pasan a `Campo`, hay que apagarlo a propósito.
     */
    const [visible, setVisible] = useState(false);
    const esSecreto = resto.type === 'password';
    const tipo = esSecreto && visible ? 'text' : resto.type;
    const entrada = (_jsx("input", { ref: ref, id: id, className: "fr-campo__entrada", "aria-invalid": error ? true : undefined, "aria-describedby": error ? idError : undefined, ...resto, type: tipo }));
    return (_jsxs("div", { className: "fr-campo", children: [_jsx("label", { className: "fr-campo__rotulo", htmlFor: id, children: rotulo }), esSecreto ? (_jsxs("div", { className: "fr-campo__secreto", children: [entrada, _jsx("button", { type: "button", className: "fr-campo__ojo", onClick: () => { setVisible((v) => !v); }, "aria-label": visible ? 'Ocultar la contraseña' : 'Ver la contraseña', "aria-pressed": visible, 
                        //  Deshabilitado con el campo: un ojo que funciona sobre un campo
                        //  que no se puede tocar enseña algo que ya no se puede cambiar.
                        disabled: resto.disabled ?? false, children: _jsx(OjoDeContrasena, { tachado: visible }) })] })) : entrada, error ? _jsx("span", { className: "fr-campo__error", id: idError, role: "alert", children: error }) : null] }));
});
/**
 * Un `<select>` con el aspecto de la casa.
 *
 * ── Por qué es un componente y no una clase que cada pantalla se ponga ──────
 * Porque el fallo que esto viene a cerrar fue exactamente ése: tres `<select>`
 * sueltos —la zona horaria del alta y los dos de los papeles— escritos en crudo
 * y sin una sola clase. Con la paleta clara pasaban por feos; con el tema
 * oscuro de la consola se quedaron **blancos sobre negro**, gritando en medio
 * de una pantalla que por lo demás está entera. Isaac: «arruinan la interfaz».
 *
 * Una clase que hay que acordarse de poner se olvida a la cuarta pantalla. Un
 * componente que ya trae el rótulo atado, el `id` y el error, no.
 *
 * ── `appearance: none` obliga a dibujar la punta de flecha ─────────────────
 * Y se dibuja con `clip-path` sobre un `<span>`, no con un SVG de fondo. Un
 * `background-image: url("data:image/svg+xml…")` tendría que llevar el color
 * escrito dentro —no hay `var()` dentro de un data URI—, y **ningún color
 * literal entra en una hoja de este repositorio** salvo en `tokens.css`. Con
 * `clip-path` el triángulo se pinta con `background: var(--fr-tinta-suave)` y
 * sigue las reglas como todo lo demás.
 */
export const Lista = forwardRef(function Lista({ rotulo, error, children, ...resto }, ref) {
    //  El mismo trato que `Campo`: el id se genera aquí para que `label` y
    //  `select` queden atados siempre, pase lo que pase en quien lo use.
    const id = useId();
    const idError = `${id}-error`;
    return (_jsxs("div", { className: "fr-campo", children: [_jsx("label", { className: "fr-campo__rotulo", htmlFor: id, children: rotulo }), _jsxs("div", { className: "fr-lista", children: [_jsx("select", { ref: ref, id: id, className: "fr-campo__entrada fr-lista__control", "aria-invalid": error ? true : undefined, "aria-describedby": error ? idError : undefined, ...resto, children: children }), _jsx("span", { className: "fr-lista__punta", "aria-hidden": "true" })] }), error ? _jsx("span", { className: "fr-campo__error", id: idError, role: "alert", children: error }) : null] }));
});
/* ── Tarjeta ─────────────────────────────────────────────────────────────── */
export const Tarjeta = ({ children, className }) => (_jsx("div", { className: ['fr-tarjeta', className].filter(Boolean).join(' '), children: children }));
/* ── Aviso ───────────────────────────────────────────────────────────────── */
export const Aviso = ({ children, acento = false }) => (_jsx("div", { className: ['fr-aviso', acento && 'fr-aviso--acento'].filter(Boolean).join(' '), role: "status", children: children }));
/* ── Etiqueta ────────────────────────────────────────────────────────────── */
export const Etiqueta = ({ children }) => (_jsx("span", { className: "fr-etiqueta", children: children }));
/**
 * Confirmaciones. Base UI se encarga de atrapar el foco, devolverlo al cerrar y
 * cerrar con `Escape` — tres cosas que a mano se olvidan y dejan a alguien
 * atrapado dentro con el teclado.
 */
/**
 * Dónde entra el foco al abrir: el primer campo **de este** diálogo, si lo hay.
 *
 * Una función y no un `ref` de quien lo usa: nadie tiene por qué saber cuál es
 * el primer campo de su formulario ni pasar una referencia hasta allí. Base UI
 * la llama con el popup ya montado.
 *
 * > [!danger] Se busca dentro del panel POR REFERENCIA, no con un
 * > `document.querySelector`
 * > La primera versión buscaba `.fr-dialogo__panel` en el documento entero, y
 * > eso es correcto sólo mientras haya **un** diálogo. Esta aplicación encadena
 * > dos —el `aria-label` de la ✕ ya lo nombra: «en una pantalla con dos diálogos
 * > encadenados»—, y entonces el foco del segundo podía aterrizar en un campo
 * > del primero, que además queda **inerte**: el navegador no lo enfoca, el foco
 * > se queda en `body` y quien navega con teclado acaba fuera de la trampa de
 * > foco.
 * >
 * > Lo destapó una prueba, no un razonamiento: la del campo marcado pasaba sola
 * > y fallaba dentro de la suite, con `activeElement` en `body`. Buscar «el
 * > último panel» lo tapaba a ratos —el orden de los portales no es una
 * > garantía—; con la referencia el problema no existe.
 *
 * Si no hay campos —una confirmación de dos botones— devuelve `true` y Base UI
 * hace lo suyo de siempre.
 *
 * **Se exporta para poder probarla sola**, y eso es lo que la referencia hace
 * posible: recibe el panel en vez de buscarlo, así que es una función pura sobre
 * un trozo de DOM. Probarla montando dos diálogos con campos en el mismo archivo
 * resultó ser una prueba que **falla al azar** —el diálogo anterior sigue en el
 * documento mientras el siguiente se monta, y jsdom deja el foco en `body`—, y
 * una suite que falla al azar es peor que una lenta. El comportamiento de punta
 * a punta sigue medido donde importa: en el diálogo del platillo del panel.
 */
export const primerCampoDe = (panel) => {
    // `T-38` · Si alguien dijo cuál es SU primer campo, manda sobre el orden del
    // documento. Lo pide el editor del platillo, que desde las calorías tiene un
    // cuadro en la columna izquierda —antes del nombre— que casi nadie viene a
    // cambiar. Sin marca, todo sigue exactamente igual que antes.
    const marcado = panel?.querySelector('[data-foco-inicial]');
    if (marcado)
        return marcado;
    // Por la clase de nuestro campo y no por `input`: Base UI monta un `<input
    // type="checkbox">` escondido dentro de cada interruptor, y con un selector
    // genérico el foco aterrizaba ahí — en la columna izquierda, antes del nombre.
    const campo = panel?.querySelector('.fr-campo__entrada, textarea, select');
    return campo ?? true;
};
export const Dialogo = ({ titulo, abierto, alCambiar, children, acciones, tituloOculto, ancho = 'angosto', encabezado, alCerrar, }) => {
    // La referencia al propio panel, que es lo que hace que el foco no pueda
    // aterrizar en otro diálogo. Ver `primerCampoDe`.
    const panel = useRef(null);
    return (_jsx(Dialog.Root, { open: abierto, onOpenChange: alCambiar, children: _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Backdrop, { className: "fr-dialogo__fondo" }), _jsxs(Dialog.Popup, { ref: panel, className: ancho === 'ancho' ? 'fr-dialogo__panel fr-dialogo__panel--ancho' : 'fr-dialogo__panel', 
                    // Al abrir, el foco va al primer campo y NO a la ✕ del encabezado, que
                    // es el primer elemento enfocable desde que existe. Un formulario se
                    // abre para escribir: aterrizar en «cerrar» obliga a un tabulador antes
                    // de la primera letra, y a quien navega con teclado le dice que lo
                    // primero que puede hacer es irse.
                    //
                    // Se resuelve AQUÍ y no con `autoFocus` en el campo: `autoFocus` pelea
                    // con la gestión de foco del diálogo y se lleva por delante la vuelta al
                    // botón que lo abrió. Lo dijeron cuatro pruebas a la vez.
                    initialFocus: () => primerCampoDe(panel.current), children: [_jsxs("div", { className: ancho === 'ancho'
                                ? 'fr-dialogo__encabezado fr-dialogo__encabezado--fijo'
                                : 'fr-dialogo__encabezado', children: [_jsxs("div", { children: [encabezado ? _jsx("p", { className: "fr-dialogo__eyebrow", children: encabezado }) : null, _jsx(Dialog.Title, { className: tituloOculto ? 'fr-solo-lectores' : 'fr-dialogo__titulo', children: titulo })] }), alCerrar ? (_jsx("button", { type: "button", className: "fr-dialogo__cerrar", "aria-label": `Cerrar ${titulo}`, onClick: alCerrar, children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 16 16", "aria-hidden": "true", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", children: _jsx("path", { d: "M4 4l8 8M12 4l-8 8" }) }) })) : null] }), children, acciones ? (_jsx("div", { className: ancho === 'ancho'
                                ? 'fr-dialogo__acciones fr-dialogo__acciones--fijo'
                                : 'fr-dialogo__acciones', children: acciones })) : null] })] }) }));
};
/* ── Interruptor ─────────────────────────────────────────────────────────── */
export const Interruptor = ({ etiqueta, activo, alCambiar, disabled }) => (_jsx(Switch.Root, { className: "fr-interruptor", checked: activo, onCheckedChange: alCambiar, 
    // `?? false` y no pasar `undefined`: con `exactOptionalPropertyTypes` no es
    // lo mismo «no lo digo» que «es indefinido», y Base UI espera un booleano.
    disabled: disabled ?? false, "aria-label": etiqueta, children: _jsx(Switch.Thumb, { className: "fr-interruptor__pulgar" }) }));
/**
 * Los iconos del menú, copiados del diseño trazo por trazo. Todos
 * `viewBox="0 0 16 16"`, sin relleno y con el trazo del color del texto, para
 * que cada uno se tiña con su tono sin pintar cuatro versiones de cada uno.
 *
 * `aria-hidden`: el rótulo va al lado y un lector que dijera «imagen, Eliminar»
 * sólo repetiría.
 */
const ICONOS_DE_ACCION = {
    ver: _jsxs(_Fragment, { children: [_jsx("path", { d: "M1.6 8s2.4-4.2 6.4-4.2S14.4 8 14.4 8s-2.4 4.2-6.4 4.2S1.6 8 1.6 8z" }), _jsx("circle", { cx: "8", cy: "8", r: "1.9" })] }),
    editar: _jsx("path", { d: "M11.2 2.6l2.2 2.2-8 8H3.2v-2.2z" }),
    // `T-39` · Dos flechas opuestas: «cambiar de lugar». No una cruz de cuatro
    // puntas —el icono clásico de arrastrar—, que es justo el gesto que `ADR-022`
    // retiró: un icono que promete arrastrar sobre una acción que abre un diálogo
    // es una promesa que la pantalla no cumple.
    mover: _jsx("path", { d: "M8 2.4v11.2M8 2.4L5.4 5M8 2.4L10.6 5M8 13.6L5.4 11M8 13.6l2.6-2.6" }),
    duplicar: _jsxs(_Fragment, { children: [_jsx("rect", { x: "5.6", y: "5.6", width: "8", height: "8", rx: "1.6" }), _jsx("path", { d: "M10.4 3.2H3.9a1.6 1.6 0 0 0-1.6 1.6v6.5" })] }),
    eliminar: _jsx(_Fragment, { children: _jsx("path", { d: "M2.8 4.4h10.4M6.4 4.4V2.9h3.2v1.5M4.3 4.4l.6 8.2h6.2l.6-8.2" }) }),
};
/**
 * El `⋮` del diseño: un botón que despliega las acciones de una fila.
 *
 * ── Por qué Base UI y no un `<div>` con `onClick` ──────────────────────────
 * Un menú tiene más reglas de las que parece: se abre con Enter **y** con las
 * flechas, se recorre con arriba/abajo, se cierra con Escape devolviendo el foco
 * al botón, atrapa el foco mientras está abierto, se cierra al pulsar fuera y se
 * coloca solo cuando no cabe debajo. Escrito a mano se olvidan tres, y las tres
 * las descubre quien navega con teclado, no quien lo escribió.
 *
 * ── El rótulo lleva el nombre de la fila ───────────────────────────────────
 * En una tabla de doce platillos hay doce de éstos. «Más acciones» repetido doce
 * veces no dice de cuál, que es como tener uno solo.
 */
export const MenuDeAcciones = ({ rotulo, acciones }) => (_jsxs(Menu.Root, { children: [_jsx(Menu.Trigger, { className: "fr-menu__disparador", "aria-label": rotulo, children: _jsx("span", { "aria-hidden": "true", children: "\u22EE" }) }), _jsx(Menu.Portal, { children: _jsx(Menu.Positioner, { side: "bottom", align: "end", sideOffset: 6, children: _jsx(Menu.Popup, { className: "fr-menu__panel", children: acciones.map((a) => (_jsxs(Menu.Item, { className: [
                            'fr-menu__opcion',
                            `fr-menu__opcion--${a.tono}`,
                            a.aparte ? 'fr-menu__opcion--aparte' : '',
                        ].filter(Boolean).join(' '), disabled: a.disabled ?? false, onClick: a.alElegir, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", "aria-hidden": "true", fill: "none", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round", strokeLinejoin: "round", children: ICONOS_DE_ACCION[a.tono] }), a.texto] }, a.texto))) }) }) })] }));
export const Navegacion = ({ secciones, actual }) => (_jsx("nav", { className: "fr-nav", "aria-label": "Secciones", children: secciones.map((s) => (_jsx("a", { href: s.href, className: "fr-nav__enlace", "aria-current": s.href === actual ? 'page' : undefined, children: s.texto }, s.href))) }));
//# sourceMappingURL=componentes.js.map